import { ChartOptionsBuilder, ChartOptionsZoomCallback } from '@utils/charts';
import Highcharts, { SeriesPieOptions } from 'highcharts';
import { getDataLabels } from './chart-data-label';
import { getLegend } from './chart-legend';
import { convertSeriesToGroupedSeries } from './chart-series-grouping';
import { barTooltipFormatter, doughnutCenterLabelFormatter, valueDevelopmentTooltipFormatter } from './chart-tooltip';

// Use of Symbol() would be prefered, but value gets lost when using Symbol
export const SymbolTimestamps = '__timestamps';
export const SymbolCategories = '__categories';
export const SymbolZoom = '__zoom';

export type ZoomInfo = {
  start: number;
  end: number;
};

function formatPercent(ctx: Highcharts.AxisLabelsFormatterContextObject, factor: number = 1): string {
  let value: string | number = ctx.value;
  if (typeof value === 'string') {
    value = Number.parseInt(value);
  }
  return `${Highcharts.numberFormat(value * factor, 2, ',', '')}`;
}

function formatPercentShort(ctx: Highcharts.AxisLabelsFormatterContextObject, factor: number = 1): string {
  let value: string | number = ctx.value;
  if (typeof value === 'string') {
    value = Number.parseInt(value);
  }
  return `${Highcharts.numberFormat(value * factor, 0, ',', '')}`;
}

// Line chart transformers

export function stockchartLineOptionsTransformer(options: Highcharts.Options): Highcharts.Options {
  const xAxis = options.xAxis as Highcharts.XAxisOptions;
  const yAxis = options.yAxis as Highcharts.YAxisOptions;
  yAxis.opposite = false;
  yAxis.labels = { distance: 9, align: 'center' };
  xAxis.labels = { distance: 12, align: 'center' };
  xAxis.tickLength = 0;
  xAxis.lineColor = 'transparent';
  yAxis.gridLineWidth = 0;

  const timestamps: number[] = xAxis.categories.map(category => Date.parse(category + 'Z'));

  const result = new ChartOptionsBuilder(<Highcharts.Options>{ ...options })
    .setAxisType('datetime', 'xAxis')
    .disableRangeSelector()
    .enableMousewheelZoom(false)
    .enableScrollbar(false)
    .enableNavigator(false)
    .setLegendEnabled(false)
    .setBorderSpacingMargin({
      spacing: [1, 1, 1, 1],
    })
    .mapSeries<Highcharts.SeriesLineOptions>(series => {
      return {
        ...series,
        data: series.data.map((value, index) => {
          return [timestamps[index], value];
        }),
      };
    })
    .get();

  result[SymbolCategories] = xAxis.categories;
  result[SymbolTimestamps] = timestamps;

  result.legend = getLegend();

  options.plotOptions.series = {
    ...options.plotOptions.series,
    dataGrouping: {
      enabled: false,
    },
  };

  return result;
}

export function stockchartPercentualLineOptionsTransformer(options: Highcharts.Options): Highcharts.Options {
  options = stockchartLineOptionsTransformer(options);

  options.tooltip.formatter = valueDevelopmentTooltipFormatter;
  options.tooltip.useHTML = true;

  options.chart.zooming = { ...options.chart.zooming, type: 'none' as any };

  options = new ChartOptionsBuilder(options).enableCompare().setLegendEnabled(true).get();
  options.chart.panning = { enabled: false };

  (options.yAxis as Highcharts.YAxisOptions).labels.formatter = ctx => formatPercentShort(ctx, 1);
  options.legend = getLegend();

  return options;
}

export const valueDevelopmentSimpleLinieOptionsTransformer: (options: Highcharts.Options) => Highcharts.Options =
  valueDevelopmentLinieOptionsTransformer(null, { interactive: false, legend: true });

export function valueDevelopmentLinieOptionsTransformer(
  callback?: ChartOptionsZoomCallback,
  config = {
    interactive: true,
    legend: true,
  }
): (options: Highcharts.Options) => Highcharts.Options {
  return options => {
    const xAxis = options.xAxis as Highcharts.XAxisOptions;
    const yAxis = options.yAxis as Highcharts.YAxisOptions;
    yAxis.opposite = false;

    yAxis.labels = { distance: 9, align: 'center' };
    xAxis.labels = { distance: 12, align: 'center' };

    xAxis.tickLength = 0;
    xAxis.lineColor = 'transparent';

    yAxis.gridLineWidth = 0;

    yAxis.labels.formatter = formatPercent;

    const timestamps: number[] = xAxis.categories.map(category => Date.parse(category + 'Z'));

    const builder = new ChartOptionsBuilder(<Highcharts.Options>{ ...options })
      .mapSeries<Highcharts.SeriesLineOptions>(series => {
        return {
          ...series,
          data: series.data.map((value, index) => {
            return [timestamps[index], value ? (value as number) * 100 : value];
          }),
        };
      })
      .setType('line')
      .enableScrollbar(false)
      .removeMarkers()
      .enableCompare()
      .disableRangeSelector();

    if (config.interactive) {
      builder.setZoomType('x');
    } else {
      builder.enableNavigator(false);
    }

    if (callback) {
      builder.setZoomCallback(callback);
    }

    const result = builder.get();

    if (!config.interactive) {
      result.chart.panning = { enabled: false };
      result.chart.zooming = {};
      result.chart.zooming.singleTouch = false;
    }

    result.chart.zooming.mouseWheel = { enabled: false };

    result.legend = getLegend();
    delete result.plotOptions.series.dataLabels;

    if (Array.isArray(result.series)) {
      for (const series of result.series) {
        series.type = 'line';
        if (series.name === 'Benchmark') {
          (series as any).enableMouseTracking = false;
        }
      }
    }

    result.tooltip.formatter = valueDevelopmentTooltipFormatter;
    result.tooltip.useHTML = true;

    result[SymbolCategories] = xAxis.categories;
    result[SymbolTimestamps] = timestamps;

    result.plotOptions.series = {
      ...result.plotOptions.series,
      dataGrouping: {
        enabled: false,
      },
    };

    return result;
  };
}

// Column chart transfomers

export const valueDevelopmentSimpleColumnOptionsTransformer: (
  options: Highcharts.Options
) => Highcharts.Options = options => {
  options.chart.type = 'column';
  options.series = options.series.map(
    series => <Highcharts.SeriesOptionsType>{ ...series, type: 'column', borderRadius: 0 }
  );

  (options.yAxis as Highcharts.XAxisOptions).labels = {
    formatter: formatPercent,
  };

  options.tooltip = {
    enabled: true,
    useHTML: true,
    animation: false,
    formatter: barTooltipFormatter,
  };

  options.plotOptions.series.dataLabels = getDataLabels();
  options.legend = getLegend();

  return options;
};

// bar chart transformers

const calculateOptimalBarChartHeight: Highcharts.ChartLoadCallbackFunction = function () {
  const barHeight = 20;
  const barGap = 20;

  // Typings lie again, pointCount is defined
  const pointCount = (this as any).pointCount;
  this.update(
    {
      chart: {
        height: barHeight * pointCount + barGap * (pointCount - 1) + this.chartHeight - this.plotHeight,
      },
    },
    true,
    undefined,
    false
  );
};

export function barChartTransformer(options: Highcharts.Options): Highcharts.Options {
  options.chart.events = {
    load: calculateOptimalBarChartHeight,
  };

  options = new ChartOptionsBuilder(options)
    .setYAxis({
      labels: { enabled: false },
      gridLineWidth: 0,
      plotLines: [
        {
          value: 0,
          width: 1,
          zIndex: 5,
        },
      ],
      title: undefined,
    })
    .get();

  options.plotOptions.bar = {
    ...options.plotOptions.bar,
    borderRadius: 0,
  };

  options.tooltip = {
    animation: false,
    enabled: true,
    useHTML: true,
    formatter: barTooltipFormatter,
  };

  options.legend = getLegend();
  options.plotOptions.series.dataLabels = getDataLabels();

  return options;
}

export function groupedBarChartTransfomer(options: Highcharts.Options): Highcharts.Options {
  options = barChartTransformer(options);
  options = convertSeriesToGroupedSeries(options);
  return options;
}

// Doughnut chart

type LegendItemObject = Highcharts.LegendItemObject & {
  label?: Highcharts.SVGElement; // Missing in highcharts exported typings
};

type HighchartsPoint = Highcharts.Point & {
  legendItem: LegendItemObject; // Missing in highcharts exported typings
};

function resolveSeriesLabels(labels: Map<string, string>, series: Highcharts.SeriesPieOptions): void {
  if (!series.custom) {
    series.custom = {};
  }

  series.data = series.data.slice();

  const keys: string[] = (series.custom['key'] = []);
  for (let index: number = 0; index < series.data.length; index++) {
    const element: number | [string, number] | Highcharts.PointOptionsObject = series.data[index];

    if (typeof element === 'number') {
      keys.push('');
      continue;
    }

    if (Array.isArray(element)) {
      keys.push(element[0]);
      element[0] = labels.get(labels[0]) ?? labels[0];
      continue;
    }

    if (element.name) {
      const name = `${element.name}`;
      keys.push(name);
      series.data[index] = { ...element, name: labels.get(name) ?? element.name };
    }
  }
}

function onMouseEnterLegendVisibility(legendItem: LegendItemObject): void {
  legendItem.symbol!.element.classList.add('pie-chart-high-opacity');
  legendItem.symbol!.element.parentElement.parentElement.classList.add('pie-chart-low-opacity-color');
  legendItem.label!.element.parentElement.classList.add('pie-chart-high-opacity');
  legendItem.label!.element.parentElement.parentElement.classList.add('pie-chart-low-opacity');
}

function onMouseLeaveLegendVisibility(legendItem: LegendItemObject): void {
  legendItem.symbol!.element.classList.remove('pie-chart-high-opacity');
  legendItem.symbol!.element.parentElement.parentElement.classList.remove('pie-chart-low-opacity-color');
  legendItem.label!.element.parentElement.classList.remove('pie-chart-high-opacity');
  legendItem.label!.element.parentElement.parentElement.classList.remove('pie-chart-low-opacity');
}

class DoughnutCenterLabel {
  private currentText: string = '';
  private element: Highcharts.SVGElement | null = null;

  constructor(public defaultText: string) {
    this.currentText = defaultText;
  }

  public setText(text: string): void {
    this.currentText = text;
    this.element.attr({ text });
  }
  public render(chart: Highcharts.Chart) {
    const series = chart.series[0] as Highcharts.SeriesPieOptions;

    if (!this.element) {
      this.element = chart.renderer
        .label(this.currentText, 0)
        .css({
          color: '#000',
          textAnchor: 'middle',
        })
        .add();
    }

    this.reflow(series, chart);
  }

  private reflow(series: Highcharts.SeriesPieOptions, chart: Highcharts.Chart): void {
    // Set font size based on chart diameter
    const fontSize: number = (series as any).center[2] / 16;
    this.element.css({
      fontSize: `${fontSize}px`,
    });

    this.element.element.style.setProperty('--pie-chart-center-label-headline-font-size', `${fontSize * 0.67}px`);

    const x: number = (series.center[0] as number) + chart.plotLeft;
    const y: number = (series.center[1] as number) + chart.plotTop - (this.element.attr('height') as number) / 2;

    this.element.attr({ x, y });
  }

}

export function doughnutChartTransformer(
  clickCallback: (key: string) => void,
  labels?: Map<string, string>,
  headline?: string,
  sum?: number | string
): (options: Highcharts.Options) => Highcharts.Options {
  return function (options) {
    const defaultText: string = sum !== undefined ? doughnutCenterLabelFormatter(sum, headline) : '';
    const customLabel: DoughnutCenterLabel = new DoughnutCenterLabel(defaultText);

    options = { ...options };

    options.series = [
      <SeriesPieOptions>{
        ...options.series[0],
        innerSize: '67%',
      },
    ];

    if (labels) {
      resolveSeriesLabels(labels, options.series[0] as SeriesPieOptions);
    }

    options.chart = {
      ...options.chart,
      colorCount: 56,

      events: {
        render: function () {
          customLabel.render(this);
        },
        load: function () {
          const series: Highcharts.Series = this.series[0];
          for (const point of series.points) {
            const legendItem: LegendItemObject = (point as HighchartsPoint).legendItem;

            const mouseOver = () => {
              onMouseEnterLegendVisibility(legendItem);
              customLabel.setText(doughnutCenterLabelFormatter(point.y));
              customLabel.render(point.series.chart);
            };

            const mouseLeave = () => {
              onMouseLeaveLegendVisibility(legendItem);
              customLabel.setText(customLabel.defaultText);
              customLabel.render(point.series.chart);
            };

            legendItem.label.element.addEventListener('mouseover', mouseOver);
            legendItem.symbol.element.addEventListener('mouseover', mouseOver);

            legendItem.label.element.addEventListener('mouseout', mouseLeave);
            legendItem.symbol.element.addEventListener('mouseout', mouseLeave);
          }
        },
      },
    };

    options.plotOptions = {
      ...options.plotOptions,
      pie: {
        dataLabels: {
          enabled: false,
        },
        borderRadius: 0,
        showInLegend: true,
      },
    };

    options.legend = {
      ...options.legend,
      symbolRadius: 10000,
      title: { text: undefined },
      enabled: true,
      layout: 'vertical',
      verticalAlign: 'middle',
      align: 'right',
      useHTML: true,
    };

    const keys = options.series[0].custom?.['key'] as string[];

    if (typeof clickCallback === 'function' && keys) {
      const processClick = (index: number): boolean => {
        const key: string = keys[index];
        clickCallback(key);
        return false;
      };

      const clickEventHandler: Highcharts.SeriesClickCallbackFunction = function (
        event: Highcharts.SeriesClickEventObject
      ) {
        const index: number = event.point.index;
        return processClick(index);
      };

      const legendClickEventHandler: Highcharts.PointLegendItemClickCallbackFunction = function (
        event: Highcharts.PointLegendItemClickEventObject
      ) {
        const index: number = event.target.index;
        return processClick(index);
      };

      options.plotOptions.pie.point = {
        ...options.plotOptions.pie.point,
        events: {
          legendItemClick: legendClickEventHandler,
        },
      };

      for (const series of options.series as SeriesPieOptions[]) {
        series.events = {
          click: clickEventHandler,
        };
      }
    }

    options.plotOptions.series = {
      ...options.plotOptions.series,
      point: {
        ...options.plotOptions.series?.point,
        events: {
          mouseOver: function () {
            const legendItem: LegendItemObject = (this as HighchartsPoint).legendItem;
            onMouseEnterLegendVisibility(legendItem);
            customLabel.setText(doughnutCenterLabelFormatter(this.y));
            customLabel.render(this.series.chart);
          },
          mouseOut: function () {
            const legendItem: LegendItemObject = (this as HighchartsPoint).legendItem;
            onMouseLeaveLegendVisibility(legendItem);
            customLabel.setText(customLabel.defaultText);
            customLabel.render(this.series.chart);
          },
        },
      },
    };

    options.tooltip = {
      enabled: false,
    };

    return options;
  };
}
